import { Box, Typography, IconButton } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Fullscreen, KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { MONITOR_COLORS } from "../../config/functions";
import { useState, useRef, useEffect } from "react";

const CHART_COLORS = [
    MONITOR_COLORS.status.error.secondary,
    MONITOR_COLORS.status.warning.secondary,
    MONITOR_COLORS.equipment.machine,
    MONITOR_COLORS.equipment.locker,
    MONITOR_COLORS.status.success
];

const ITEMS_PER_PAGE = 4;

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    return (
        <Box sx={{
            backgroundColor: MONITOR_COLORS.secondary,
            border: '1px solid ' + MONITOR_COLORS.tertiary,
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            maxWidth: 200,
        }}>
            <Typography sx={{ fontSize: '0.75rem', color: MONITOR_COLORS.text.secondary, mb: 0.25, wordBreak: 'break-word' }}>
                {payload[0].name}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: MONITOR_COLORS.text.primary }}>
                {payload[0].value} erros ({payload[0].payload.percentual}%)
            </Typography>
        </Box>
    );
};

const CustomLegend = ({ tipos }) => {
    const [page, setPage] = useState(0);

    const totalPages = Math.ceil(tipos.length / ITEMS_PER_PAGE);
    const start = page * ITEMS_PER_PAGE;
    const visibleItems = tipos.slice(start, start + ITEMS_PER_PAGE);

    const canUp = page > 0;
    const canDown = page < totalPages - 1;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
        }}>
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.6,
                px: 1,
            }}>
                {visibleItems.map((item, index) => {
                    const globalIndex = start + index;
                    return (
                        <Box key={globalIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: CHART_COLORS[globalIndex % CHART_COLORS.length],
                                flexShrink: 0,
                            }} />
                            <Typography sx={{
                                fontSize: { xs: '0.65rem', xl: '0.72rem' },
                                color: MONITOR_COLORS.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                minWidth: 0,
                            }}>
                                {item.erro}
                            </Typography>
                            <Typography sx={{
                                fontSize: { xs: '0.65rem', xl: '0.72rem' },
                                color: MONITOR_COLORS.text.primary,
                                fontWeight: 600,
                                flexShrink: 0,
                                ml: 0.5,
                            }}>
                                {item.percentual}%
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {totalPages > 1 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    mt: 0.5,
                    flexShrink: 0,
                }}>
                    <IconButton
                        size="small"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={!canUp}
                        sx={{
                            color: canUp ? MONITOR_COLORS.text.primary : MONITOR_COLORS.text.secondary,
                            opacity: canUp ? 1 : 0.3,
                            p: 0.25,
                            '&:hover': {
                                backgroundColor: MONITOR_COLORS.tertiary,
                            },
                        }}
                    >
                        <KeyboardArrowUp sx={{ fontSize: 16 }} />
                    </IconButton>

                    <Typography sx={{
                        fontSize: '0.6rem',
                        color: MONITOR_COLORS.text.secondary,
                        minWidth: 28,
                        textAlign: 'center',
                    }}>
                        {page + 1}/{totalPages}
                    </Typography>

                    <IconButton
                        size="small"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={!canDown}
                        sx={{
                            color: canDown ? MONITOR_COLORS.text.primary : MONITOR_COLORS.text.secondary,
                            opacity: canDown ? 1 : 0.3,
                            p: 0.25,
                            '&:hover': {
                                backgroundColor: MONITOR_COLORS.tertiary,
                            },
                        }}
                    >
                        <KeyboardArrowDown sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};

export function MonitorErrorsType({ requestErrors }) {
    const tipos = requestErrors?.tipos ?? [];

    const chartData = tipos.map(item => ({
        name: item.erro,
        value: item.total,
        percentual: item.percentual,
    }));

    return (
        <Box sx={{
            border: '1px solid ' + MONITOR_COLORS.tertiary,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box',
            marginTop: '2vh'
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                flexShrink: 0,
                backgroundColor: MONITOR_COLORS.primary,
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid ' + MONITOR_COLORS.tertiary,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Fullscreen sx={{ color: MONITOR_COLORS.text.primary, fontSize: { xs: 18, xl: 22 }, marginRight: '1vw' }} />
                    <Typography sx={{ fontWeight: 700, color: MONITOR_COLORS.text.primary, fontSize: { xs: '0.9rem', xl: '1rem' } }}>
                        Tipos de Erro
                    </Typography>
                </Box>
            </Box>

            <Box sx={{
                flex: 1,
                backgroundColor: MONITOR_COLORS.secondary,
                borderRadius: '0 0 12px 12px',
                p: { xs: 1, xl: 1.5 },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                gap: 0,
            }}>
                {chartData.length === 0 ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: MONITOR_COLORS.text.secondary }}>
                            Sem erros nas últimas 12h ✓
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ flex: '0 0 55%', minHeight: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius="35%"
                                        outerRadius="60%"
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {chartData.map((_, index) => (
                                            <Cell
                                                key={index}
                                                fill={CHART_COLORS[index % CHART_COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <CustomLegend tipos={tipos} />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}